import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, MenuItem, Button, Chip, CircularProgress, Divider, Tooltip,
  FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import * as docuRuleApi from '../../lib/api/docuRule';

// 화면 묶음. 서버의 DOCU_GRP 와 짝이 맞아야 한다.
const GROUPS = [
  { key: 'SALES', label: '영업', desc: '고객에게 나가는 문서가 많습니다' },
  { key: 'MANUFACTURING', label: '제조', desc: '대부분 사내에서만 씁니다' },
  { key: 'ETC', label: '기타', desc: '' },
];

const RESET_OPTIONS = [
  { value: 'DAY', label: '매일 1번부터' },
  { value: 'MONTH', label: '매월 1번부터' },
  { value: 'YEAR', label: '매년 1번부터' },
  { value: 'NEVER', label: '계속 이어서' },
];

// 누르면 커서 자리에 꽂히는 토큰들. 설명이 없으면 무엇을 쓸 수 있는지 알 방법이 없다.
// 2026-10-20 (bito 기획서 5/07, GDS_CIS_SE1_25_00001_01 포맷): {PRDTYP}/{ITEM}/{DEPT}/{REV:n} 추가.
const TOKENS = [
  { t: '{PREFIX}', d: '회사 표시' },
  { t: '{TYP}', d: '문서 종류' },
  { t: '{PRDTYP}', d: '제품 구분 (상품/제품/용역/유지보수) - 아래 "제품 구분 코드"에서 편집' },
  { t: '{ITEM}', d: '아이템 구분 (CISCO/팔로알토 …) - 아래 "아이템 코드"에서 편집' },
  { t: '{DEPT}', d: '작성자 부서 코드 - 아래 "부서 코드"에서 편집' },
  { t: '{YYYY}', d: '연 4자리' },
  { t: '{YY}', d: '연 2자리' },
  { t: '{MM}', d: '월' },
  { t: '{DD}', d: '일' },
  { t: '{SEQ:3}', d: '일련번호 3자리' },
  { t: '{SEQ:4}', d: '일련번호 4자리' },
  { t: '{SEQ:5}', d: '일련번호 5자리' },
  { t: '{REV:2}', d: '차수 2자리 - 번호 맨 끝에만, {SEQ} 와 함께 써야 함(재발행 시 올라감)' },
];

const DocuRuleContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState([]);
  const [prefix, setPrefix] = useState('');
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});   // docuTyp -> { pattern, seqReset, seqScope, sample, saving }

  // 미리보기용 표본 맥락. {PRDTYP}/{ITEM}/{DEPT} 가 실제로 뭐가 찍히는지 보려면 값이 있어야 한다.
  const [sampleProductYn, setSampleProductYn] = useState('상품');
  const [sampleIsMaintenance, setSampleIsMaintenance] = useState(false);
  const [sampleItem, setSampleItem] = useState('CISCO');
  const [sampleDeptUuid, setSampleDeptUuid] = useState('');

  const [prdTypCodes, setPrdTypCodes] = useState([]); // [{SLC_CD, REV_VAL_1, ITEM_NM}]
  const [itemCodes, setItemCodes] = useState([]);
  const [deptCodes, setDeptCodes] = useState([]);      // [{DPT_UUID, DPT_NM, DPT_CD}]
  const [codeDrafts, setCodeDrafts] = useState({});    // key -> { value, saving }

  const sampleCtx = () => ({
    prjTyp: sampleIsMaintenance ? 'M' : 'N',
    productYn: sampleIsMaintenance ? '' : sampleProductYn,
    solutionTyp: sampleItem,
    deptUuid: sampleDeptUuid,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, prdTypRes, itemRes, deptRes] = await Promise.all([
        docuRuleApi.getDocuRules(),
        docuRuleApi.getCodeMapList('DOCU_PRDTYP'),
        docuRuleApi.getCodeMapList('DOCU_ITEM'),
        docuRuleApi.getDeptCodeList(),
      ]);
      setRules(rulesRes.data?.list || []);
      setPrefix(rulesRes.data?.prefix || '');
      setPrdTypCodes(prdTypRes.data?.list || []);
      setItemCodes(itemRes.data?.list || []);
      setDeptCodes(deptRes.data?.list || []);
      setDrafts({});
      setCodeDrafts({});
    } catch {
      enqueueSnackbar('문서번호 규칙을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const draftOf = (rule) => drafts[rule.docuTyp] || {
    pattern: rule.pattern || '',
    seqReset: rule.seqReset || 'MONTH',
    seqScope: rule.seqScope || 'GLOBAL',
    sample: rule.sample || '',
  };

  const setDraft = (docuTyp, patch) =>
    setDrafts((d) => ({ ...d, [docuTyp]: { ...d[docuTyp], ...patch } }));

  // 미리보기는 서버가 만든다. 화면에서 따로 조립하면 두 벌이 되고 언젠가 어긋난다.
  //
  // base를 항상 채워 넣는다 - 아코디언을 "펼치기만" 해도(패턴을 직접 안 건드려도) 이 함수가
  // 불린다(처음 펼칠 때 표본 미리보기를 보여주려고). 그때 draftOf(rule)의 기본값 없이
  // {sample: ...} 조각만 저장하면 drafts[docuTyp]가 "패턴 없는 반쪽 객체"로 굳어버려서,
  // 그 다음부터 draftOf가 그 반쪽 객체를 (기본값 대신) 돌려주며 일련번호/범위 선택이
  // 빈 값으로 보이는 버그가 났었다(2026-10-20, Chrome 검증 중 발견).
  const refreshSample = async (rule, pattern) => {
    const base = draftOf(rule);
    try {
      const res = await docuRuleApi.previewDocuRule(pattern, rule.docuTyp, sampleCtx());
      setDraft(rule.docuTyp, { ...base, pattern, sample: res.data?.sample || '' });
    } catch {
      setDraft(rule.docuTyp, { ...base, pattern, sample: '' });
    }
  };

  // 표본(제품구분/아이템/부서)을 바꾸면 펼쳐 둔 규칙들의 미리보기도 다시 받는다.
  useEffect(() => {
    rules.forEach((rule) => {
      const cur = drafts[rule.docuTyp];
      if (cur) refreshSample(rule, cur.pattern);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleProductYn, sampleIsMaintenance, sampleItem, sampleDeptUuid]);

  const handlePatternChange = (rule, value) => {
    setDraft(rule.docuTyp, { ...draftOf(rule), pattern: value });
    refreshSample(rule, value);
  };

  const insertToken = (rule, token) => {
    const cur = draftOf(rule);
    const next = `${cur.pattern}${token}`;
    setDraft(rule.docuTyp, { ...cur, pattern: next });
    refreshSample(rule, next);
  };

  const handleSave = async (rule) => {
    const cur = draftOf(rule);
    setDraft(rule.docuTyp, { ...cur, saving: true });
    try {
      await docuRuleApi.saveDocuRule(rule.docuTyp, {
        pattern: cur.pattern,
        seqReset: cur.seqReset,
        seqScope: cur.seqScope,
      });
      enqueueSnackbar(`${rule.docuNm} 번호 모양을 바꿨습니다.`, { variant: 'success' });
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || '저장에 실패했습니다.';
      enqueueSnackbar(msg, { variant: 'error' });
      setDraft(rule.docuTyp, { ...cur, saving: false });
    }
  };

  const changed = (rule) => {
    const cur = draftOf(rule);
    return cur.pattern !== (rule.pattern || '')
      || cur.seqReset !== (rule.seqReset || '')
      || cur.seqScope !== (rule.seqScope || 'GLOBAL');
  };

  // ── {PRDTYP}/{ITEM} 코드, 부서 코드 편집 ──────────────────────

  const codeKey = (blcCd, slcCd) => `${blcCd}::${slcCd}`;

  const handleCodeSave = async (blcCd, slcCd, listSetter, list) => {
    const key = codeKey(blcCd, slcCd);
    const value = codeDrafts[key]?.value ?? '';
    setCodeDrafts((d) => ({ ...d, [key]: { ...d[key], saving: true } }));
    try {
      await docuRuleApi.saveCodeMap(blcCd, slcCd, value);
      listSetter(list.map((row) => (row.SLC_CD === slcCd ? { ...row, REV_VAL_1: value } : row)));
      setCodeDrafts((d) => ({ ...d, [key]: { value, saving: false } }));
      enqueueSnackbar('코드를 바꿨습니다.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '코드 저장에 실패했습니다.', { variant: 'error' });
      setCodeDrafts((d) => ({ ...d, [key]: { ...d[key], saving: false } }));
    }
  };

  const handleDeptCodeSave = async (dptUuid) => {
    const key = `DEPT::${dptUuid}`;
    const value = codeDrafts[key]?.value ?? '';
    setCodeDrafts((d) => ({ ...d, [key]: { ...d[key], saving: true } }));
    try {
      await docuRuleApi.saveDeptCode(dptUuid, value);
      setDeptCodes((list) => list.map((row) => (row.DPT_UUID === dptUuid ? { ...row, DPT_CD: value } : row)));
      setCodeDrafts((d) => ({ ...d, [key]: { value, saving: false } }));
      enqueueSnackbar('부서 코드를 바꿨습니다.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '부서 코드 저장에 실패했습니다.', { variant: 'error' });
      setCodeDrafts((d) => ({ ...d, [key]: { ...d[key], saving: false } }));
    }
  };

  const renderCodeTable = (title, blcCd, list, listSetter) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>원래 값</TableCell>
            <TableCell>짧은 코드</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((row) => {
            const key = codeKey(blcCd, row.SLC_CD);
            const value = codeDrafts[key]?.value ?? row.REV_VAL_1 ?? '';
            return (
              <TableRow key={row.SLC_CD}>
                <TableCell>{row.SLC_CD}</TableCell>
                <TableCell>
                  <TextField
                    value={value}
                    size="small"
                    sx={{ width: 100 }}
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                    onChange={(e) => setCodeDrafts((d) => ({
                      ...d, [key]: { ...d[key], value: e.target.value },
                    }))}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    disabled={codeDrafts[key]?.saving}
                    onClick={() => handleCodeSave(blcCd, row.SLC_CD, listSetter, list)}
                  >
                    저장
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ m: 1 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>번호 앞에 붙는 회사 표시</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={prefix || '설정 안 됨'} color={prefix ? 'primary' : 'default'} size="small" />
          <Typography variant="caption" color="text.secondary">
            {'{PREFIX}'} 자리에 들어갑니다. 공통코드 BASE / COMPANY / DOCU_PREFIX 에서 바꿉니다.
          </Typography>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          미리보기 표본 - {'{PRDTYP}'}/{'{ITEM}'}/{'{DEPT}'} 가 실제로 어떻게 찍히는지 보는 값입니다
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControlLabel
            control={(
              <Switch
                checked={sampleIsMaintenance}
                onChange={(e) => setSampleIsMaintenance(e.target.checked)}
              />
            )}
            label="유지보수 문서로 보기"
          />
          {!sampleIsMaintenance && (
            <TextField
              select label="제품 구분" value={sampleProductYn} size="small" sx={{ minWidth: 140 }}
              onChange={(e) => setSampleProductYn(e.target.value)}
            >
              {prdTypCodes.filter((c) => c.SLC_CD !== '유지보수').map((c) => (
                <MenuItem key={c.SLC_CD} value={c.SLC_CD}>{c.SLC_CD} ({c.REV_VAL_1 || '?'})</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select label="아이템 구분" value={sampleItem} size="small" sx={{ minWidth: 160 }}
            onChange={(e) => setSampleItem(e.target.value)}
          >
            {itemCodes.map((c) => (
              <MenuItem key={c.SLC_CD} value={c.SLC_CD}>{c.SLC_CD} ({c.REV_VAL_1 || '?'})</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="부서" value={sampleDeptUuid} size="small" sx={{ minWidth: 160 }}
            onChange={(e) => setSampleDeptUuid(e.target.value)}
          >
            <MenuItem value="">(선택 안 함 - XXX)</MenuItem>
            {deptCodes.map((d) => (
              <MenuItem key={d.DPT_UUID} value={d.DPT_UUID}>{d.DPT_NM} ({d.DPT_CD || '?'})</MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      {renderCodeTable('제품 구분 코드 - {PRDTYP}', 'DOCU_PRDTYP', prdTypCodes, setPrdTypCodes)}
      {renderCodeTable('아이템 코드 - {ITEM}', 'DOCU_ITEM', itemCodes, setItemCodes)}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>부서 코드 - {'{DEPT}'}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>부서</TableCell>
              <TableCell>코드</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {deptCodes.map((d) => {
              const key = `DEPT::${d.DPT_UUID}`;
              const value = codeDrafts[key]?.value ?? d.DPT_CD ?? '';
              return (
                <TableRow key={d.DPT_UUID}>
                  <TableCell>{d.DPT_NM}</TableCell>
                  <TableCell>
                    <TextField
                      value={value}
                      size="small"
                      sx={{ width: 100 }}
                      InputProps={{ sx: { fontFamily: 'monospace' } }}
                      onChange={(e) => setCodeDrafts((d2) => ({
                        ...d2, [key]: { ...d2[key], value: e.target.value },
                      }))}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" disabled={codeDrafts[key]?.saving} onClick={() => handleDeptCodeSave(d.DPT_UUID)}>
                      저장
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        바꾼 모양은 앞으로 만들어지는 번호에만 적용됩니다. 이미 발급된 번호는 그대로 남습니다.
      </Typography>

      {GROUPS.map((g) => {
        const items = rules.filter((r) => r.docuGrp === g.key);
        if (items.length === 0) return null;
        return (
          <Box key={g.key} sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
              <Typography variant="subtitle1" fontWeight={600}>{g.label}</Typography>
              <Typography variant="caption" color="text.secondary">{g.desc}</Typography>
            </Box>

            {items.map((rule) => {
              const cur = draftOf(rule);
              return (
                <Accordion key={rule.docuTyp} disableGutters
                  onChange={(_, expanded) => { if (expanded) refreshSample(rule, cur.pattern); }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap' }}>
                      <Typography sx={{ minWidth: 96 }}>{rule.docuNm}</Typography>
                      <Chip label={rule.docuTyp} size="small" variant="outlined" />
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {rule.sample || rule.pattern}
                      </Typography>
                      {changed(rule) && <Chip label="변경됨" size="small" color="warning" />}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="번호 모양"
                        value={cur.pattern}
                        onChange={(e) => handlePatternChange(rule, e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{ sx: { fontFamily: 'monospace' } }}
                      />

                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {TOKENS.map((tk) => (
                          <Tooltip key={tk.t} title={tk.d}>
                            <Chip
                              label={tk.t}
                              size="small"
                              variant="outlined"
                              onClick={() => insertToken(rule, tk.t)}
                              sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ))}
                      </Box>

                      <Divider />

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          select
                          label="일련번호"
                          value={cur.seqReset}
                          onChange={(e) => setDraft(rule.docuTyp, { ...cur, seqReset: e.target.value })}
                          size="small"
                          sx={{ minWidth: 160 }}
                        >
                          {RESET_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </TextField>

                        <Tooltip title="PREFIX를 고르면 {SEQ} 앞 칸의 실제 값 조합(예: 제품구분+아이템+부서+연)마다 00001부터 따로 셉니다.">
                          <TextField
                            select
                            label="일련번호 범위"
                            value={cur.seqScope}
                            onChange={(e) => setDraft(rule.docuTyp, { ...cur, seqScope: e.target.value })}
                            size="small"
                            sx={{ minWidth: 220 }}
                          >
                            <MenuItem value="GLOBAL">전체 공통</MenuItem>
                            <MenuItem value="PREFIX">앞 칸 조합별로 따로</MenuItem>
                          </TextField>
                        </Tooltip>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            이렇게 나옵니다
                          </Typography>
                          <Typography sx={{ fontFamily: 'monospace' }}>
                            {cur.sample || '—'}
                          </Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />

                        <Button
                          variant="contained"
                          size="small"
                          disabled={!changed(rule) || cur.saving}
                          onClick={() => handleSave(rule)}
                        >
                          {cur.saving ? <CircularProgress size={18} /> : '저장'}
                        </Button>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default DocuRuleContainer;
